import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  Search, Users, Building2, Trophy, Loader2, ArrowUpRight, CalendarClock, FileSignature,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

const StageColors = {
  sourced: "#71717A", screening: "#A1A1AA", submitted: "#002EA6", interview: "#F5A623",
  offer: "#10B981", hired: "#10B981", rejected: "#4b1113",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const primary = [
    { label: "Active Searches", value: stats.active_searches, icon: Search, to: "/app/searches" },
    { label: "Candidates", value: stats.total_candidates, icon: Users, to: "/app/candidates" },
    { label: "Organizations", value: stats.total_organizations, icon: Building2, to: "/app/organizations" },
    { label: "Placements", value: stats.placements, icon: Trophy, to: "/app/searches" },
  ];

  const chartData = Object.entries(stats.stage_counts).map(([k, v]) => ({ stage: k, count: v }));

  return (
    <div>
      <PageHeader overline={`Welcome back, ${user?.full_name?.split(" ")[0] || "Recruiter"}`} title="Dashboard" />
      <div className="p-8 space-y-px bg-black/10">
        {/* North star row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px">
          {primary.map((s) => (
            <button
              key={s.label}
              onClick={() => navigate(s.to)}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
              className="group bg-white p-6 text-left hover:bg-[#F4F4F5] transition-colors duration-150 relative"
            >
              <s.icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
              <p className="font-heading font-black text-5xl mt-6 tracking-tight">{s.value}</p>
              <p className="overline mt-2">{s.label}</p>
              <ArrowUpRight className="h-4 w-4 absolute top-6 right-6 text-muted-foreground/0 group-hover:text-primary transition-colors" />
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-px">
          {/* Pipeline chart */}
          <div className="lg:col-span-2 bg-white p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="overline">Pipeline distribution</p>
                <p className="font-heading font-bold text-xl mt-1">{stats.in_pipeline} candidates in motion</p>
              </div>
              <div className="flex gap-6 text-center">
                <div><p className="font-heading font-black text-2xl text-[#F5A623]">{stats.interviews}</p><p className="overline text-[0.55rem]">Interviews</p></div>
                <div><p className="font-heading font-black text-2xl text-[#10B981]">{stats.offers}</p><p className="overline text-[0.55rem]">Offers</p></div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ left: -20 }}>
                <XAxis dataKey="stage" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#00000015" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#00000008" }} contentStyle={{ background: "#09090B", border: "1px solid #00000015", borderRadius: 0, color: "#fff" }} />
                <Bar dataKey="count" radius={0}>
                  {chartData.map((e) => <Cell key={e.stage} fill={StageColors[e.stage] || "#002EA6"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recent searches */}
          <div className="bg-white p-6">
            <p className="overline mb-4">Recent searches</p>
            <div className="space-y-px">
              {stats.recent_searches.length === 0 && <p className="text-sm text-muted-foreground">No searches yet.</p>}
              {stats.recent_searches.map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/app/searches/${s.id}`)}
                  className="w-full text-left p-3 border-b border-black/5 hover:bg-black/5 transition-colors group"
                >
                  <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">{s.title}</p>
                  <p className="overline text-[0.55rem] mt-1">{s.organization_name} · {s.role_type}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
